import type { ReactNode } from "react";
import { ChevronRight, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@jbnl/shared-ui";
import { buttonVariants } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton, SkeletonRegion } from "@/components/ui/skeleton";
import { MicrosoftMark } from "@/components/brand/ProviderMark";
import { DevSignIn } from "./DevSignIn";
import { Inloglink } from "./Inloglink";
import { LocalSignIn } from "./LocalSignIn";
import {
  INLOGLINK_SCHEME,
  LOCAL_SCHEME,
  isKnownProvider,
  providerLabel,
  useProviders,
  type ProviderScheme,
} from "./useProviders";

/** Ranked by how many people hold each: the account of a korps or regio, the inloglink, then a password. */
export function SignIn() {
  const { t } = useTranslation("session");

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("signIn.title")}</CardTitle>
        <CardDescription>{t("signIn.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <SignInOptions />
      </CardContent>
    </Card>
  );
}

function SignInOptions() {
  const { t } = useTranslation("session");
  const { data: schemes, isPending } = useProviders();

  const oidcSchemes = oidcSchemesOf(schemes);
  const hasSso = oidcSchemes.length > 0;
  const hasInloglink = schemes?.includes(INLOGLINK_SCHEME) ?? false;
  const hasLocal = schemes?.includes(LOCAL_SCHEME) ?? false;

  if (isPending) {
    return (
      <SkeletonRegion className="space-y-4">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-40 w-full" />
      </SkeletonRegion>
    );
  }

  if (!hasSso && !hasInloglink && !hasLocal) {
    return <p className="text-sm text-muted-foreground">{t("signIn.noneConfigured")}</p>;
  }

  return (
    <div className="space-y-6">
      {hasSso && <SsoDoor schemes={oidcSchemes} />}

      {hasSso && hasInloglink && <Divider>{t("signIn.fallback")}</Divider>}

      {hasInloglink && (
        <div className="rounded-lg bg-muted/50 p-4">
          <p className="mb-3 flex items-center gap-2 text-sm font-medium">
            <Mail className="size-4 text-brand-blue" />
            {t("signIn.inloglink")}
          </p>
          <Inloglink />
        </div>
      )}

      {hasLocal && (hasSso || hasInloglink ? <PasswordFold /> : <LocalSignIn />)}

      <DevSignIn />
    </div>
  );
}

function oidcSchemesOf(schemes: string[] | undefined): ProviderScheme[] {
  return (
    schemes?.filter(
      (scheme): scheme is ProviderScheme =>
        scheme !== LOCAL_SCHEME && scheme !== INLOGLINK_SCHEME && isKnownProvider(scheme),
    ) ?? []
  );
}

function SsoDoor({ schemes }: { schemes: ProviderScheme[] }) {
  const { t } = useTranslation("session");
  const returnUrl = `${window.location.pathname}${window.location.search}`;

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className="font-semibold">{t("signIn.sso.title")}</p>
        <p className="text-sm text-muted-foreground">{t("signIn.sso.hint")}</p>
      </div>
      <div className="flex flex-col gap-2">
        {schemes.map((scheme) => (
          <a
            key={scheme}
            className={cn(
              buttonVariants({ variant: "outline" }),
              "h-12 w-full gap-3 border-2 border-brand-blue text-base font-semibold",
            )}
            href={`/api/auth/challenge/${scheme}?returnUrl=${encodeURIComponent(returnUrl)}`}
            data-testid={`signin-${scheme}`}
          >
            <MicrosoftMark />
            {t("signIn.withProvider", { provider: providerLabel(scheme) })}
          </a>
        ))}
      </div>
    </div>
  );
}

function Divider({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground">
      <span className="h-px flex-1 bg-border" />
      {children}
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

function PasswordFold() {
  const { t } = useTranslation("session");

  return (
    <details className="group text-sm">
      <summary className="flex cursor-pointer list-none items-center gap-2 text-muted-foreground hover:text-foreground [&::-webkit-details-marker]:hidden">
        <ChevronRight className="size-4 transition group-open:rotate-90" />
        {t("signIn.local")}
      </summary>
      <div className="pt-4">
        <LocalSignIn />
      </div>
    </details>
  );
}
